import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetServiceQuery,
  useGetHomepageSettingsQuery,
} from "../redux/services/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useTranslation } from "../hooks/useTranslation";
import { trackServicePage } from "../utils/analytics";
import AOS from "aos";
import "aos/dist/aos.css";

// Helper function to parse Draft.js content to HTML
const parseDraftContent = (content) => {
  if (!content) return "";

  try {
    // If content is already a string, return it
    if (typeof content === "string") {
      // Check if it's JSON string
      if (content.startsWith("{") && content.endsWith("}")) {
        const parsed = JSON.parse(content);
        return parseDraftBlocks(parsed);
      }
      return content;
    }

    // If content is an object, parse it directly
    if (typeof content === "object") {
      return parseDraftBlocks(content);
    }

    return content;
  } catch (error) {
    console.error("Error parsing content:", error);
    return content;
  }
};

const parseDraftBlocks = (draftContent) => {
  if (!draftContent.blocks || !Array.isArray(draftContent.blocks)) {
    return "";
  }

  return draftContent.blocks
    .map((block) => {
      if (block.type === "unstyled") {
        return `<p>${block.text}</p>`;
      } else if (block.type === "header-one") {
        return `<h1>${block.text}</h1>`;
      } else if (block.type === "header-two") {
        return `<h2>${block.text}</h2>`;
      } else if (block.type === "header-three") {
        return `<h3>${block.text}</h3>`;
      } else if (block.type === "unordered-list-item") {
        return `<li>${block.text}</li>`;
      } else if (block.type === "ordered-list-item") {
        return `<li>${block.text}</li>`;
      } else {
        return `<p>${block.text}</p>`;
      }
    })
    .join("");
};

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isArabic } = useTranslation();

  // Use RTK Query to fetch service
  const {
    data: serviceResponse,
    isLoading: loading,
    error,
  } = useGetServiceQuery(id);
  const { data: settingsResponse } = useGetHomepageSettingsQuery();

  const service = serviceResponse?.service || null;
  const logoUrl = settingsResponse?.settings?.branding?.logo || null;

  // Initialize AOS and track page visit
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  // Track service page visit
  useEffect(() => {
    if (service) {
      trackServicePage(service.id, service.title);
    }
  }, [service]);

  // Set page title and meta keywords
  useEffect(() => {
    if (service) {
      // Set page title
      document.title = service.title || "Service";

      // Remove ALL existing meta keywords tags (in case HeadTags or other components added them)
      const existingMetaKeywords = document.querySelectorAll(
        'meta[name="keywords"]'
      );
      existingMetaKeywords.forEach((tag) => tag.remove());

      // Create new meta keywords tag with service keywords
      if (service.keywords || service.title) {
        const metaKeywords = document.createElement("meta");
        metaKeywords.setAttribute("name", "keywords");

        // Combine service title and keywords
        const keywords = service.keywords
          ? `${service.title}, ${service.keywords}`
          : service.title;

        metaKeywords.setAttribute("content", keywords);
        document.head.appendChild(metaKeywords);
      }
    }

    // Cleanup function
    return () => {
      document.title = "Silver Fort";
      // Remove meta keywords on unmount
      const metaKeywords = document.querySelectorAll('meta[name="keywords"]');
      metaKeywords.forEach((tag) => tag.remove());
    };
  }, [service]);

  const handleBackToServices = () => {
    navigate("/services");
  };

  const handleContactUs = () => {
    navigate("/#contact");
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  if (loading) {
    return (
      <div className="service-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading service details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-detail-page">
        <div className="container">
          <div className="error-container">
            <h1>Service Not Found</h1>
            <p>
              The service you're looking for doesn't exist or has been removed.
            </p>
            <button className="btn" onClick={handleBackToServices}>
              Back to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      <Header />
      <div className="container">
        {/* Main Content */}
        <div className="service-main-content">
          <div
            className="service-image-section"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            {service.image_url ? (
              <img
                src={service.image_url}
                alt={service.title}
                className="service-hero-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = "flex";
                  }
                }}
              />
            ) : null}
            <div
              className="service-image-placeholder"
              style={{
                display: service.image_url ? "none" : "flex",
              }}
            >
              <div className="placeholder-content">
                <div className="placeholder-icon">🔧</div>
                <p>{t("common.noImageAvailable")}</p>
              </div>
            </div>
          </div>

          <div
            className="service-details"
            data-aos="fade-left"
            data-aos-delay="300"
          >
            <h1 className="service-title">
              {isArabic && service.title_ar ? service.title_ar : service.title}
            </h1>
            {service.category && (
              <div className="service-category">
                <span className="category-badge">
                  {isArabic && service.category.title_ar
                    ? service.category.title_ar
                    : service.category.title}
                </span>
              </div>
            )}
            <div className="service-description">
              <div
                dangerouslySetInnerHTML={{
                  __html: parseDraftContent(
                    isArabic && service.content_ar
                      ? service.content_ar
                      : service.content
                  ),
                }}
              />
            </div>

            <div
              className="service-cta"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="cta-content">
                <h2>{t("services.needThisService")}</h2>
                <p>{t("services.serviceDescription")}</p>

                <div className="cta-buttons">
                  <button className="btn btn-primary" onClick={handleContactUs}>
                    {t("contact.contactUsNow")}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={handleBackToServices}
                  >
                    {t("services.viewAll")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ServiceDetail;
